<template>
  <div
    v-if="fieldMappingModel"
    class="MappingQuestionsContainer"
  >
    <h2 class="Heading">
      Map Form question to {{ integration.name }}
    </h2>
    <section>
      <div class="MappingContainer">
        <div class="Headers u-flex">
          <h3>Question</h3>
          <h3>Map to</h3>
        </div>
        <div
          v-if="showDuplicateMappingError"
          class="error"
        >
          You may have some duplicate mappings
        </div>
        <div
          v-for="(question, index) in fieldMappingModel"
          :key="index"
          class="MappingQuestions u-flex"
        >
          <rmp-autocomplete
            v-model="fieldMappingModel[index].html_form_input_id"
            index="html_form_input_id"
            class="u-noMargin"
            option-key="label_without_html_tags"
            :items="eventQuestions"
          />
          <div class="Divider">
            <span class="Icon Icon-chevron-circle-right" />
          </div>
          <rmp-autocomplete
            v-if="!isZapierIntegration"
            v-model="fieldMappingModel[index].mapped_to"
            index="value"
            class="u-noMargin"
            option-key="label"
            :items="integrationFieldsToMap"
          />
          <rmp-input
            v-if="isZapierIntegration"
            v-model="fieldMappingModel[index].mapped_to"
            class="u-noMargin"
          />
          <span
            class="Icon Icon-times"
            @click="removeQuestion(index)"
          />
        </div>
        <button
          class="Button Button--transparent"
          @click="addNewQuestion"
        >
          + Add Question
        </button>
        <button
          class="Button Button--transparent u-floatRight RemoveAllButton"
          @click="removeAllQuestions"
        >
          Remove All
        </button>
      </div>
      <div class="Footer u-flex u-flex-spaceBetween">
        <button
          class="Button Button--green-outline"
          @click="$emit('question-mapping:cancel')"
        >
          Cancel
        </button>
        <div class="">
          <span class="StepNumber ">{{ stepNumber }}</span>
          <button
            :disabled="showDuplicateMappingError || fieldMappingModel.length === 0"
            class="Button Button--green"
            @click="handleSubmit"
          >
            {{ buttonText }}
          </button>
        </div>
      </div>
    </section>
  </div>
</template>
<script>
import { mapState } from 'vuex';
import rmpAutocomplete from '@rmpenterprise/form-autocomplete-select';
import rmpInput from '@rmpenterprise/form-input';

export default {
  components: {
    rmpAutocomplete,
    rmpInput,
  },
  props: {
    integration: {
      type: Object,
      required: true,
      default() {
        return {};
      },
    },
    eventQuestions: {
      type: Array,
      required: true,
      default() {
        return [];
      },
    },
  },
  data() {
    return {
      mappedQuestions: this.integration.questions_mapping || [],
      fieldMappingModel: null,
    };
  },

  computed: {
    ...mapState('events', {
      integrations: (state) => state.lists.integrations,
    }),
    stepNumber() {
      if (this.isZapierIntegration) return '1/3';
      return '1/2';
    },
    buttonText() {
      return this.integration.id ? 'Update Mapping' : 'Create Mapping';
    },
    isZapierIntegration() {
      return this.integration.web_forms_integration_type_uuid === 'zapier' || this.integration.uuid === 'zapier';
    },
    integrationFieldsToMap() {
      return this.integrations.find((integrationType) => this.integration.name === integrationType.name).fields_to_map;
    },
    showDuplicateMappingError() {
      const hasDuplicates = this.selectedEventQuestionLabels.length !== new Set(this.selectedEventQuestionLabels).size
        || this.selectedIntegrationQuestionLabels.length !== new Set(this.selectedIntegrationQuestionLabels).size;
      return hasDuplicates;
    },
    selectedEventQuestionLabels() {
      return this.fieldMappingModel.map((q) => q.html_form_input_id).filter((n) => n);
    },
    selectedIntegrationQuestionLabels() {
      return this.fieldMappingModel.map((q) => q.mapped_to).filter((n) => n);
    },
  },
  mounted() {
    this.populateFields();
  },
  methods: {
    populateFields() {
      // check integration.questions_mapping for existing integration fields
      if (this.integration.questions_mapping) {
        this.fieldMappingModel = this.integration.questions_mapping;
        return;
      }

      if (this.isZapierIntegration) {
        // Zapier integration fields are pre populated
        this.fieldMappingModel = this.eventQuestions.map((q) => {
          return { html_form_input_id: q.html_form_input_id, mapped_to: q.html_form_input_id };
        });
        return;
      }

      this.fieldMappingModel = this.eventQuestions.map((q) => {
        return { html_form_input_id: q.html_form_input_id, mapped_to: null };
      });
    },
    addNewQuestion() {
      this.fieldMappingModel.push({
        html_form_input_id: null,
        mapped_to: null,
      });
    },
    removeAllQuestions() {
      this.fieldMappingModel = [];
    },
    removeQuestion(index) {
      this.fieldMappingModel.splice(index, 1);
    },
    handleSubmit() {
      this.$emit('question-mapping:submit', this.fieldMappingModel);
    },
  },
};
</script>
<style scoped lang="scss">

::v-deep .vs__selected-options {
  width: 147px;
}

::v-deep .selected-tag {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
    display: block;
}

::v-deep .dropdown-menu li a {
  overflow: hidden;
  text-overflow: ellipsis;
}

.Divider {
    height: 1px;
    position: relative;
    width: 101px;
    background: #A7A7A7;
    align-self: center;
    margin: 0 10px;

    .Icon {
      position: absolute;
      color: #A7A7A7;
      top: -9px;
      right: 15px;
      font-size: 1.4rem;
      background: white;
    }
}

.IntegrationActions {
  align-items: center;
  justify-content: flex-end;

  .IntegrationToggle {
    margin-bottom: 0;
    width: auto;
  }
}

.MappingQuestionsContainer {
  border: 1px solid #E3E3E3;
  border-radius: 8px;
  background-color: #FFFFFF;
  box-shadow: 0 13px 29px 0 rgba(0,0,0,0.11);
  padding-bottom: 25px;

  .RemoveAllButton {
    margin-right: 20px;
  }

  .Heading {
    padding: 21px 30px;
    border-bottom: 1px solid lightgray;
    margin: 0;
  }

  .MappingContainer {
  .Headers {

    h3 {
    width: 100%;
       &:first-child {
        margin-right: 55px;
    }
    }

  }
  .MappingQuestions {
    margin: 15px 0;
    align-items: center;

    .Icon-times {
      margin-left: 10px;
      cursor: pointer;
    }
  }
}
}
.StepNumber {
  margin-right: 5px;
}
.Footer {
  margin-top: 40px;
}
</style>
